/**
 * Parser for the AI agent's rich transcript (the `rich_transcript` Arcus asset):
 * NDJSON, one `{timestamp, role, title, data}` event per line. Mirrors the event
 * selection and titles of the oai-realtime console-ui page so the two read the same.
 *
 * The transcript, prompts and context are PHI: nothing here logs or stores them.
 */

type JsonObject = Record<string, unknown>;

interface RawEvent {
  timestamp: string;
  role: string;
  title: string;
  data: JsonObject;
}

type EventLevel = 'normal' | 'warn' | 'error';

export interface ConsoleEvent {
  /** Position in the displayed list; stable key for rows. */
  index: number;
  /** Elapsed time since call start, as written by the agent ("H:MM:SS.ffffff"). */
  timestamp: string;
  seconds: number;
  role: string;
  /** The event's own title (e.g. "response.create"). */
  kind: string;
  /** Display title, enriched with the event's key value. */
  title: string;
  data: JsonObject;
  level: EventLevel;
  /** Byte range of this user utterance inside the user_utterances WAV data chunk. */
  userClip?: { offset: number; length: number };
  /** Agent speech span, in seconds from call start. */
  agentSpan?: { start: number; end: number };
  /** Set on response.create events, for the prompt viewer. */
  turnId?: string;
  /** Lower-cased title + payload, built once so search does not re-serialise every keystroke. */
  searchText: string;
}

export interface SpeechSegment {
  start: number;
  end: number;
  text: string;
  speaker: 'user' | 'agent';
}

export interface ParsedRichTranscript {
  events: ConsoleEvent[];
  /** Latest context from state_transition / context_update. */
  context: JsonObject | null;
  agenda: JsonObject | null;
  coveredTopics: string[];
  usage: JsonObject | null;
  speech: SpeechSegment[];
  /** Call start (epoch ms) from the call_metadata line, for wall-clock times. */
  startTime: number | null;
}

const FAILED_CALL_STATUSES = ['no-answer', 'busy', 'failed', 'canceled'];

const isObject = (v: unknown): v is JsonObject =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const parseMaybeJson = (v: unknown): unknown => {
  if (typeof v !== 'string') return v;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
};

/** Parses Python's str(timedelta): "H:MM:SS[.ffffff]" or "N day(s), H:MM:SS[.ffffff]". */
export function parseTimedelta(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(?:(-?\d+) days?, )?(\d+):(\d{2}):(\d{2}(?:\.\d+)?)$/);
  if (!match) return null;
  const [, days, h, m, s] = match;
  return Number(days ?? 0) * 86400 + Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/** Adds every numeric leaf of `src` into `dst`, recursively. */
function addNumbers(dst: JsonObject, src: JsonObject): void {
  for (const [key, value] of Object.entries(src)) {
    if (typeof value === 'number') {
      dst[key] = (typeof dst[key] === 'number' ? (dst[key] as number) : 0) + value;
    } else if (isObject(value)) {
      if (!isObject(dst[key])) dst[key] = {};
      addNumbers(dst[key] as JsonObject, value);
    }
  }
}

function parseLines(text: string): RawEvent[] {
  const events: RawEvent[] = [];
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    try {
      const parsed = JSON.parse(trimmed);
      if (isObject(parsed) && typeof parsed.title === 'string') {
        events.push({
          timestamp: typeof parsed.timestamp === 'string' ? parsed.timestamp : '',
          role: typeof parsed.role === 'string' ? parsed.role : '',
          title: parsed.title,
          data: isObject(parsed.data) ? parsed.data : {},
        });
      }
    } catch {
      // A partially written last line (live file) or a corrupt line is skipped.
    }
  }
  return events;
}

/** Background-model analyses keyed by the turn they analysed. */
function backgroundByTurn(events: RawEvent[]): Map<string, JsonObject> {
  const byTurn = new Map<string, JsonObject>();
  for (const e of events) {
    if (e.title !== 'background_model.response') continue;
    const inner = isObject(e.data.data) ? e.data.data : {};
    const id = inner.conversation_turn_id ?? inner.correlation_id;
    if (typeof id === 'string') byTurn.set(id, e.data);
  }
  return byTurn;
}

interface ParseState {
  usage: JsonObject;
  coveredTopics: string[];
  context: JsonObject | null;
  agenda: JsonObject | null;
}

const optString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

/** Spoken text of one response output item, for the title. */
function outputText(item: JsonObject, data: JsonObject): string | undefined {
  const content = Array.isArray(item.content) ? item.content.filter(isObject) : [];
  const first = content[0];
  if (first?.type === 'audio') return optString(first.transcript);
  return data.role === 'agent' ? optString(first?.text) : undefined;
}

/** Folds usage into the running total and replaces the response with its outputs. */
function responseDoneTitle(event: RawEvent, data: JsonObject, usage: JsonObject): string {
  const response = isObject(data.response) ? data.response : {};
  if (isObject(response.usage)) addNumbers(usage, response.usage);
  const items = (Array.isArray(response.output) ? response.output : []).filter(isObject);

  const outputs = items.map(o =>
    o.type === 'function_call'
      ? { type: o.type, name: o.name, arguments: parseMaybeJson(o.arguments) }
      : { type: o.type, role: o.role, content: o.content },
  );
  data.response = { status: response.status, status_details: response.status_details, outputs };
  if (items.length === 0) return event.title;

  const parts = [event.title, items.map(o => String(o.type ?? '')).join(',')];
  const spoken = items.map(o => outputText(o, data)).filter((t): t is string => t !== undefined);
  if (spoken.length > 0) parts.push(`"${spoken.join(',')}"`);
  return parts.join(' | ');
}

function serverTitle(event: RawEvent, data: JsonObject, state: ParseState): string | null {
  switch (event.title) {
    case 'response.done':
      return responseDoneTitle(event, data, state.usage);
    case 'category_response':
      return event.title;
    case 'agent.speech':
    case 'user.speech':
      return `${event.title} | ${String(data.transcript ?? '')}`;
    case 'call_state_update':
      return data.CallDuration
        ? `${event.title} | ${String(data.CallStatus ?? '')} | ${String(data.CallDuration)}s`
        : `${event.title} | ${String(data.CallStatus ?? '')}`;
    default:
      return null;
  }
}

function clientTitle(event: RawEvent, data: JsonObject): string {
  switch (event.title) {
    case 'function_call_response':
      data.arguments = parseMaybeJson(data.arguments);
      return `${event.title} | ${String(data.name ?? '')}`;
    case 'first_audible_word':
      return `${event.title} | ${String(data.delay ?? '')}s`;
    default:
      return event.title;
  }
}

function stateTransitionTitle(event: RawEvent, data: JsonObject, state: ParseState): string {
  const next = isObject(data.new) ? data.new : data;
  const topic = String(next.topic ?? '');
  const last = state.coveredTopics[state.coveredTopics.length - 1];
  if (!last || !last.includes(topic)) state.coveredTopics.push(`${event.timestamp} ${topic}`);
  if (isObject(data.context)) state.context = data.context;
  return `state | topic:${topic} ${String(next.state_idx ?? '')}`;
}

function agentTitle(event: RawEvent, data: JsonObject, state: ParseState): string | null {
  switch (event.title) {
    case 'user_vad':
    case 'assistant_vad':
      return `${event.title} | ${String(data.state ?? '')}`;
    case 'state_transition':
      return stateTransitionTitle(event, data, state);
    case 'context_update':
      if (isObject(data.context)) state.context = data.context;
      return null;
    case 'agenda':
      state.agenda = data;
      return event.title;
    case 'backchannel':
      return `backchannel | word:${String(data.word ?? '')}`;
    default:
      return null;
  }
}

/** Display title for an event, or null when the console hides it. */
function titleFor(event: RawEvent, state: ParseState): string | null {
  switch (event.role) {
    case 'server':
      return serverTitle(event, event.data, state);
    case 'client':
      return clientTitle(event, event.data);
    case 'agent':
      return agentTitle(event, event.data, state);
    default:
      return event.title;
  }
}

function timeSpan(data: JsonObject): { start: number; end: number } | undefined {
  const start = parseTimedelta(data.time_start);
  const end = parseTimedelta(data.time_end);
  return start !== null && end !== null && end > start ? { start, end } : undefined;
}

/** Speech segment for the synced transcript, from a speech event whether shown or not. */
function speechSegment(event: RawEvent): SpeechSegment | null {
  if (event.title !== 'user.speech' && event.title !== 'agent.speech') return null;
  const span = timeSpan(event.data);
  const text = optString(event.data.transcript)?.trim();
  if (!span || !text) return null;
  return { ...span, text, speaker: event.title === 'user.speech' ? 'user' : 'agent' };
}

/** Attaches the background-model analysis of this turn; reports whether it flagged it. */
function applyBackground(
  title: string,
  data: JsonObject,
  analysis: JsonObject | undefined,
): { title: string; flagged: boolean } {
  if (!analysis) return { title, flagged: false };
  data.background_model = isObject(analysis.data) ? analysis.data : analysis;
  const rating = analysis.conversation_turn_rating;
  const flagged = analysis.has_mismatch === true || (typeof rating === 'number' && rating < 5);
  if (!title.includes('reasoning_response') && !title.includes('function_call_response')) {
    return { title, flagged };
  }
  return { title: `${title} | has background analysis${flagged ? ' ⚠️' : ''}`, flagged };
}

function errorMessage(data: JsonObject): unknown {
  if (data.message !== undefined) return data.message;
  const err = data.error;
  return isObject(err) ? (err.type ?? err.message) : err;
}

function userClipOf(data: JsonObject): ConsoleEvent['userClip'] {
  const offset = data.user_utterance_offset;
  const length = data.user_utterance_len;
  return typeof offset === 'number' && typeof length === 'number' && length > 0
    ? { offset, length }
    : undefined;
}

function agentSpanOf(event: RawEvent): ConsoleEvent['agentSpan'] {
  const { data } = event;
  const isAgentSpeech =
    data.role === 'agent' || data.type === 'agent.speech' || event.title === 'agent.speech';
  return isAgentSpeech ? timeSpan(data) : undefined;
}

function levelFor(event: RawEvent, title: string, flagged: boolean): EventLevel {
  if (title.includes('error')) return 'error';
  const failedCall =
    event.title === 'call_state_update' &&
    FAILED_CALL_STATUSES.includes(String(event.data.CallStatus));
  return failedCall || flagged ? 'warn' : 'normal';
}

/** Builds the display event, or null when the console hides it. */
function toConsoleEvent(
  event: RawEvent,
  index: number,
  state: ParseState,
  background: Map<string, JsonObject>,
): ConsoleEvent | null {
  const baseTitle = titleFor(event, state);
  if (baseTitle === null) return null;

  // parseLines built these objects for this call alone, so enriching them in place is safe.
  const { data } = event;
  const turnId = optString(data.conversation_turn_id);
  const correlationId = turnId ?? optString(data.correlation_id);
  const bg = applyBackground(
    baseTitle,
    data,
    correlationId ? background.get(correlationId) : undefined,
  );

  let { title } = bg;
  if (title.includes('error')) {
    const message = errorMessage(data);
    if (message !== undefined) title += ` | ${String(message)}`;
  }

  return {
    index,
    timestamp: event.timestamp,
    seconds: parseTimedelta(event.timestamp) ?? 0,
    role: event.role,
    kind: event.title,
    title,
    data,
    level: levelFor(event, title, bg.flagged),
    userClip: userClipOf(data),
    agentSpan: agentSpanOf(event),
    turnId: event.title === 'response.create' ? turnId : undefined,
    searchText: `${title} ${JSON.stringify(data)}`.toLowerCase(),
  };
}

/** Parses the rich transcript into display events and the side-panel state. */
export function parseRichTranscript(text: string): ParsedRichTranscript {
  const raw = parseLines(text);
  const background = backgroundByTurn(raw);
  const state: ParseState = { usage: {}, coveredTopics: [], context: null, agenda: null };
  const events: ConsoleEvent[] = [];
  const speech: SpeechSegment[] = [];

  let startTime: number | null = null;
  for (const event of raw) {
    if (event.title === 'call_metadata') startTime = parseStartTime(event.data.start_time);
    if (event.title === 'call_metadata' || event.title === 'background_model.response') continue;
    const segment = speechSegment(event);
    if (segment) speech.push(segment);
    const displayed = toConsoleEvent(event, events.length, state, background);
    if (displayed) events.push(displayed);
  }

  const { usage, coveredTopics, context, agenda } = state;
  return {
    events,
    context,
    agenda,
    coveredTopics,
    usage: Object.keys(usage).length > 0 ? usage : null,
    speech: dedupeSpeech(speech),
    startTime,
  };
}

/**
 * The agent writes Python's str(datetime) ("2026-10-16 10:23:45.123456[+00:00]").
 * A value without an offset is UTC, the agent's clock.
 */
export function parseStartTime(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  let iso = value.trim().replace(' ', 'T');
  iso = iso.replace(/(\.\d{3})\d+/, '$1');
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(iso)) iso += 'Z';
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** Elapsed, local and UTC times of an event, as console-ui's timestamp tooltip shows them. */
export function timestampTooltip(event: ConsoleEvent, startTime: number | null): string {
  if (startTime === null) return `Elapsed: ${event.timestamp}`;
  const at = new Date(startTime + event.seconds * 1000);
  const local = at.toLocaleString(undefined, {
    hour12: false,
    fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);
  return `Elapsed: ${event.timestamp}\nLocal: ${local}\nUTC: ${at.toISOString()}`;
}

/** The parsed value of a JSON object/array string, or undefined when it is not one. */
function parseJsonString(value: string): unknown {
  const t = value.trim();
  const looksLikeJson =
    (t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'));
  if (!looksLikeJson) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return undefined;
  }
}

/** Parses JSON-looking strings nested in a payload, so embedded objects read as JSON. */
export function deepFormatJson(value: unknown, depth = 0): unknown {
  if (depth > 10) return value;
  if (typeof value === 'string') {
    const parsed = parseJsonString(value);
    return parsed === undefined ? value : deepFormatJson(parsed, depth + 1);
  }
  if (Array.isArray(value)) return value.map(v => deepFormatJson(v, depth + 1));
  if (!isObject(value)) return value;
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [k, deepFormatJson(v, depth + 1)]),
  );
}

/** Same segment written twice (live rewrite) keeps the last copy. */
function dedupeSpeech(segments: SpeechSegment[]): SpeechSegment[] {
  const byKey = new Map<string, SpeechSegment>();
  for (const s of segments) byKey.set(`${s.start.toFixed(6)}|${s.text.slice(0, 20)}`, s);
  return [...byKey.values()].sort((a, b) => a.start - b.start);
}

/** The system prompt the agent sent for a turn (the matching response.create). */
export function findSystemPrompt(events: ConsoleEvent[], turnId: string): string | null {
  const event = events.find(e => e.kind === 'response.create' && e.turnId === turnId);
  const prompt = event?.data.system_message;
  return typeof prompt === 'string' ? prompt : null;
}

/** Case-insensitive matcher for the query: a regex when valid, else plain text. */
export function buildMatcher(query: string): (text: string) => boolean {
  if (!query) return () => true;
  try {
    const re = new RegExp(query, 'i');
    return text => re.test(text);
  } catch {
    const needle = query.toLowerCase();
    return text => text.toLowerCase().includes(needle);
  }
}

/** Events whose title or payload matches the query. */
export function filterEvents(events: ConsoleEvent[], query: string): ConsoleEvent[] {
  if (!query) return events;
  const matches = buildMatcher(query);
  return events.filter(e => matches(e.searchText));
}

/** Formats seconds as M:SS.s (or H:MM:SS.s past an hour). */
export function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = (seconds % 60).toFixed(1).padStart(4, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}
